import "assets/css/privacy.css";

const Terms = () => {
  return (
    <article className="privacy-page">
      <main className="privacy-page__main">
        <section className="privacy-page__block privacy-page__head">
          <h1 className="privacy-page__block-title">Terms of Use</h1>
          <p className="privacy-page__block-descr">
            <strong>Last Updated on August 31st, 2025</strong>
          </p>
          <p className="privacy-page__block-descr">
            Welcome to the Terms of Use (these "Terms") for the website
            <a
              href="https://www.twotwentyk.io"
              className="privacy-page__block-link"
            >
              www.twotwentyk.io
            </a>
            and its subdomains (the "Website"), a gaming platform for prediction
            game TwoTwentyK (the "Platform"), and all other applications,
            content, smart contracts, tools, features, and functionality offered
            on or through the Website and the Platform (collectively with the
            Website and the Platform, the "Services"), in each case operated by
            or on behalf of 10XCHI, Inc. ("10XCHI", "Company", "we", "us", or
            "our").
          </p>
          <p className="privacy-page__block-descr">
            <strong>
              BY (I) USING THE SERVICES OR ANY PART OF THEM, OR (II) BUYING,
              SELLING OR OTHERWISE ACQUIRING OR TRANSFERRING A COLLECTIBLE (AS
              DEFINED BELOW), YOU ARE CONFIRMING THAT YOU UNDERSTAND AND AGREE
              TO BE BOUND BY ALL OF THESE TERMS.
            </strong>
          </p>
          <p className="privacy-page__block-descr">
            Any changes to these Terms will be in effect as of the "Last
            Updated" date referred to at the top of this page. You should review
            these Terms before using the Services or purchasing any Collectible.
            Your continued use of the Services after the "Last Updated" date
            will constitute your acceptance of and agreement to such changes.
          </p>
          <p className="privacy-page__block-descr">
            By signing up for an Account (as defined in Section 2) on the
            Platform, using a Digital Wallet (as defined in Section 2), or
            otherwise using or accessing the Services, you acknowledge that you
            have read and agree to these Terms and any terms and conditions
            incorporated herein by reference, and affirm that you meet the
            eligibility requirements set forth in Section 6, that you accept and
            are bound by these Terms, and if you are using the Services on
            behalf of an organization or company, that you have the legal
            authority to bind any such organization or company to these Terms.
            You may not use the Services if you: (i) do not agree to these
            Terms; (ii) do not meet the eligibility requirements set forth in
            Section 6; or (iii) are prohibited from accessing or using the
            Services or any of this Platform’s contents, products or services by
            applicable law.
          </p>
          <p className="privacy-page__block-descr">
            <strong>
              THESE TERMS CONTAIN AN ARBITRATION AND CLASS ACTION WAIVER
              PROVISION (SEE SECTION 17). PLEASE REVIEW THE ARBITRATION AND
              CLASS ACTION WAIVER PROVISION CAREFULLY, SINCE IT AFFECTS YOUR
              RIGHTS. BY USING THE SERVICES OR ANY PART OF THEM, YOU UNDERSTAND
              AND AGREE TO BE BOUND BY THE ARBITRATION AND CLASS ACTION WAIVER
              PROVISION.
            </strong>
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">1. Definitions</h2>
          <p className="privacy-page__block-descr">
            The following definitions are used in these Terms:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              <strong>"Collectibles"</strong> means digital assets created by us
              or you in connection with the Services, the ownership of which is
              tracked via the Polygon blockchain, and the ownership of which
              will give you certain rights and privileges within the Services.
            </li>
            <li className="privacy-page__block-list-item">
              <strong>"Content"</strong> means content, materials, art, design,
              and drawings, in any form or media, including, without limitation,
              images, photographs, audio, or videos.
            </li>
            <li className="privacy-page__block-list-item">
              <strong>"Digital Wallet"</strong> means a unique custodial digital
              wallet on the Polygon blockchain which corresponds to each
              Account, which enables the holder of the corresponding Account to
              purchase, store, and engage in transactions on the Platform using
              the Services, and to connect Collectibles with the Services.
            </li>
            <li className="privacy-page__block-list-item">
              <strong>"Marketplace"</strong> means the online peer-to-peer
              marketplace offered through the Platform, which provides various
              Marketplace Offerings.
            </li>
            <li className="privacy-page__block-list-item">
              <strong>"Marketplace Offerings"</strong> include various
              Services-related offerings, including, but not limited to, direct
              sales of Collectibles to you and the facilitation of peer-to-peer
              sales conducted via decentralized or third- party exchanges or
              protocols whereby buyers and sellers can buy, sell, and trade
              Collectibles with each other.
            </li>
            <li className="privacy-page__block-list-item">
              <strong>"NFTs"</strong> means non-fungible tokens.
            </li>
            <li className="privacy-page__block-list-item">
              <strong>"Prediction"</strong> means a forward-looking statement
              about the outcome of an event which is the result of the gathering
              and skillful analysis of one or more data points.
            </li>
            <li className="privacy-page__block-list-item">
              <strong>"Third Party Services"</strong> means third-party content,
              applications, and other services provided to us or you, by or
              through third parties, through among other things, links to
              third-party websites, including, but not limited to: (a) content,
              applications, advertising, data, statistics, and other content,
              and (b) decentralized smart contract protocols or exchanges.
            </li>
          </ul>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            2. User Account; Digital Wallet; Financial Transactions
          </h2>
          <p className="privacy-page__block-descr">
            <strong>User Account.</strong> You will be required to create an
            account to use the Services (your "Account"). In creating and
            maintaining your Account, you agree to provide us with accurate,
            complete, and updated information. You are solely responsible for
            any activity on your Account and for maintaining the confidentiality
            and security of your password. We are not liable for any acts or
            omissions by you in connection with your Account.
          </p>
          <p className="privacy-page__block-descr">
            <strong>Digital Wallet.</strong> Upon creating your Account, we will
            create a corresponding Digital Wallet. The Digital Wallets are made
            available through and operated by Venly, whose terms of service, to
            which your use of the Digital Wallet will be subject, can be found
            at{" "}
            <a
              href="https://www.venly.io/legal/terms-of-service"
              target="_blank"
              rel="noreferrer"
            >
              https://www.venly.io/legal/terms-of-service
            </a>
            . Your Digital Wallet may only be used in connection with our
            Platform, and you will be unable to transfer Collectibles to other
            Digital Wallets registered with our Platform or other third-party
            platforms. We accept no responsibility or liability to you in
            connection with your use of a Digital Wallet and make no
            representations or warranties regarding how the Services will
            operate with the Digital Wallet. We will have access to your Digital
            Wallet and you agree to permit us to perform actions involving your
            Digital Wallet on your behalf in the course of delivering the
            Services
          </p>
          <p className="privacy-page__block-descr">
            <strong>Financial Transactions on Platform.</strong> Most payments,
            cash withdrawals, or financial transactions that you engage in via
            the Services are currently conducted through the following
            third-party payment providers whose Terms of Use may be found at the
            links provided below:
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              Venly: &nbsp;
              <a
                href="https://www.venly.io/legal/terms-of-service"
                target="_blank"
                rel="noreferrer"
              >
                https://www.venly.io/legal/terms-of-service
              </a>
            </li>
            <li className="privacy-page__block-list-item">
              Onramper: &nbsp;
              <a
                href="https://www.onramper.com/terms-conditions"
                target="_blank"
                rel="noreferrer"
              >
                https://www.onramper.com/terms-conditions
              </a>
            </li>
          </ul>

          <p className="privacy-page__block-descr">
            We have no control over withdrawals, payments, or other
            transactions, nor do we have the ability to reverse any withdrawals,
            payments, or other transactions. We have no liability to you or to
            any third party for any claims or damages that may arise as a result
            of any withdrawals, payments, or other transactions that you engage
            in via the Services. We do not provide refunds for any purchases
            that you might make on or through the Platform —whether for
            Collectibles or anything else.
          </p>
          <p className="privacy-page__block-descr">
            <strong>Gas Fees.</strong> Every transaction on the Polygon
            blockchain requires the payment of a transaction fee (each, a "Gas
            Fee"). The Gas Fees fund the network of computers that run the
            decentralized Polygon blockchain. We will pay the Gas Fees for any
            transactions on the Platform; however, this may change in the future
            through an update to these Terms, in which case, you may, in the
            future, need to pay all or a portion of Gas Fees.
          </p>
          <p className="privacy-page__block-descr">
            <strong>Responsibility for Taxes.</strong> You will be solely
            responsible to pay any and all sales, use, value-added and other
            taxes, duties, and assessments (except taxes on our net income) now
            or hereafter claimed or imposed by any governmental authority
            (collectively, the "Taxes") associated with your use of the
            Services. Upon making any cash withdrawals, you may be requested to
            complete an affidavit of eligibility and a liability/publicity
            release (unless prohibited by law) and/or appropriate tax forms and
            forms of identification, including but not limited to a driver's
            license, proof of residence, and/or any information relating to
            payment/deposit accounts as our payment service provider may
            reasonably request in order to complete the withdrawal of deposits.
            Failure to comply with this requirement may result in
            disqualification and forfeiture of any deposits. Disqualification or
            forfeiture of any deposits may also occur in the event it is
            determined that you did not comply, in any manner, with these Terms
            of Use.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            3. Use of the Platform; Security
          </h2>
          <p className="privacy-page__block-descr">
            <strong>Collectibles.</strong> You can purchase, earn, collect,
            showcase, transfer, and trade Collectibles, and use them in the
            Platform in various ways that we make available on the Platform. We
            make no promises or guarantees regarding the functionality, utility,
            or value of any Collectibles, and such Collectibles may evolve and
            change as we update and modify the Services and the Platform. You
            can also buy Collectibles from other Collectible owners within the
            Marketplace. You are responsible for all payments, fees, and costs
            when engaging in any transactions involving Collectibles, including,
            without limitation, the purchase price, transaction fees, fees
            associated with your secondary or subsequent sales of Collectibles,
            and all other fees associated with your use of the Collectibles or
            the Services.
          </p>
          <p className="privacy-page__block-descr">
            We reserve the right to modify, delete, and/or replace the Content
            linked to, or associated with, your Collectibles at any time with or
            without notice, as follows: (i) adding or removing third party brand
            names, logos, or other intellectual property; (ii) updating factual
            or statistical information; (iii) changing designations associated
            with the Collectible; (iv) modifications at the request or demand of
            a third party; or (v) correcting any errors in the Content.
          </p>
          <p className="privacy-page__block-descr">
            We make no guarantees or representations that the collectibles
            offered on our platform will increase in value over time. These
            assets may decrease in value or hold no value at all, and you should
            make your own assessments before engaging in any transactions.
          </p>
          <p className="privacy-page__block-descr">
            <strong>Marketplace.</strong> We provide the Marketplace to enable
            Marketplace Offerings. We may charge a transaction fee on any or all
            peer-to-peer sales that occur via the Marketplace or that otherwise
            involve Collectibles (which may be calculated as a percentage of the
            overall sale price of the Collectibles). To facilitate transactions
            via the Marketplace, we may integrate with one or more Third Party
            Services, including, but not limited to, decentralized smart
            contract protocols, which facilitate the exchange of cryptocurrency
            and other assets through the Polygon blockchain. We do not own or
            control any Third Party Services, or the Polygon blockchain, we do
            not control the transfer of cryptocurrency using Third Party
            Services, and we do not have the ability to cancel or reverse
            certain transactions. Except as may otherwise be provided in these
            Terms, we do not endorse purchases or sales of Collectibles or other
            Marketplace Offerings outside of the Services. We have no control
            over the conduct of buyers, sellers, Third Party Services, and any
            other users of the Marketplace. We accept no liability for any
            aspect of the buyer and seller peer-to-peer interaction on the
            Marketplace.
          </p>
          <p className="privacy-page__block-descr">
            <strong>Promotional Codes.</strong> We may offer certain promotional
            codes, referral codes, discount codes, coupon codes, or similar
            offers (“Promotional Codes”) that may be redeemed for discounts on
            future Marketplace Offerings, or other features or benefits related
            to the Services, subject to any additional terms that we establish.
            You agree that Promotional Codes: (a) must be used in a lawful
            manner; (b) must be used for the intended audience and purpose; (c)
            may not be duplicated, sold, or transferred in any manner, or made
            available by you to the general public (whether posted to a public
            forum, coupon collecting service, or otherwise), unless expressly
            permitted by us; (d) may be disabled or have additional conditions
            applied to them by us, at any time for any reason without liability
            to us; (e) may only be used pursuant to the specific terms that we
            establish for such Promotional Code; (f) are not valid for cash or
            other credits or points; and (g) may expire prior to your use.
          </p>
          <p className="privacy-page__block-descr">
            <strong>Changes and Pricing.</strong> We may, at any time, revise or
            change the pricing, availability, specifications, content,
            descriptions, or features of any Marketplace Offerings sold by us,
            and to correct pricing errors that may inadvertently occur (and to
            cancel any orders in our sole discretion that were purchased with
            pricing errors). All such changes shall be effective immediately
            upon posting of such new Marketplace Offerings prices to the
            Platform. While we attempt to be as accurate as we can in our
            descriptions for Marketplace Offerings, we do not warrant that
            Marketplace Offerings descriptions are accurate, complete, reliable,
            current, or error-free. The inclusion of any Marketplace Offerings
            for purchase through the Services at a particular time does not
            imply or warrant that the Marketplace Offerings will be available at
            any other time. We reserve the right to change prices for
            Marketplace Offerings displayed on the Services at any time.
          </p>
          <p className="privacy-page__block-descr">
            <strong>Purchases Made for Consumptive Use.</strong> You acknowledge
            and affirm that you are acquiring Marketplace Offerings, whether
            through the Marketplace or otherwise through the Services, for
            purposes of use in connection with the Services and, in the case of
            Collectibles, to play the game or for other collectible purposes,
            and in each case not for any investment or speculative purposes. Any
            economic benefit that may be derived from appreciation in the value
            of an Offering or Collectible is incidental to obtaining it for its
            use in connection with the Services or its collectible purpose.
          </p>
          <p className="privacy-page__block-descr">
            <strong>
              ALL SALES FINAL. ALL PURCHASES MADE THROUGH THE SERVICES ARE FINAL
              AND NON-REFUNDABLE, EXCEPT AS REQUIRED BY APPLICABLE LAW OR
              REGULATION (AND IN SUCH INSTANCES WHERE REQUIRED BY APPLICABLE
              LAW, OUR ABILITY TO ISSUE ANY SUCH REFUND IS LIMITED TO WHETHER
              THE APPLICABLE SMART CONTRACT ENABLES US TO REASONABLY REVERSE OR
              CANCEL A TRANSACTION). NEVERTHELESS, YOU ACKNOWLEDGE THAT WE MAY
              NOT HAVE THE ABILITY TO REVERSE OR CANCEL A TRANSACTION AND ARE
              NOT REQUIRED OR OBLIGATED TO PROVIDE A REFUND OF ANY PURCHASE FOR
              ANY REASON, AND THAT YOU WILL NOT RECEIVE MONEY OR OTHER
              COMPENSATION FOR TIME, ITEMS, AMOUNTS, OR VALUES IN YOUR ACCOUNT,
              EVEN IF YOUR ACCOUNT IS CLOSED, WHETHER SUCH CLOSURE WAS VOLUNTARY
              OR INVOLUNTARY.
            </strong>
          </p>
          <p className="privacy-page__block-descr">
            <strong>Marketplace Rules and Additional Terms.</strong> In addition
            to these Terms, your use of the Marketplace and any Marketplace
            Offerings will be governed by any additional terms and rules that we
            may create and make available and to which you agree to be bound.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            4. Location of Our Privacy Policy and Other Terms
          </h2>
          <p className="privacy-page__block-descr">
            <strong>Privacy Policy.</strong> Privacy Policy. Our Privacy Policy
            describes how we handle the information you provide to us when you
            use the Services. For an explanation of our privacy practices,
            please visit our Privacy Policy located at
            <a
              href="https://game.twotwentyk.io/privacies/privacy-policy."
              target="_blank"
              rel="noreferrer"
            >
              https://game.twotwentyk.io/privacies/privacy-policy.
            </a>
            .
          </p>
          <p className="privacy-page__block-descr">
            <strong>Other Terms.</strong> Reserved.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">5. Rewards and Prizes</h2>
          <p className="privacy-page__block-descr">
            <strong>Rewards.</strong> We will pay Rewards and Prizes in cash,
            cryptocurrency, through the delivery of additional Collectibles, or
            through a combination of one or more of the same (“Rewards” and
            “Prizes”). Rewards and Prizes shall be deposited into the Digital
            Wallet in which the Collectible entitled to the Reward or Prize is
            held within three (3) business days following the validation of a
            Prediction. For purposes of clarity and by way of example, if a
            holder of a Collectible creates a Prediction that becomes entitled
            to a Reward, and prior to receiving the Reward or Prize, such holder
            transfers the applicable Collectible to another Digital Wallet, the
            Reward or Prize shall be deposited into the Digital Wallet to which
            the Collectible has been transferred, rather into the Digital Wallet
            of the original holder of the Collectible.
          </p>
          <p className="privacy-page__block-descr">
            <strong>Prize Calculations.</strong> Prizes shall be awarded based
            on the face value of the redeemable Prize Card Token (“Prize Card
            Token”), unless the Prize Card Token us eligible for a bonus or
            multiplier, all of which shall be set forth on the Website.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">6. Eligibility</h2>
          <p className="privacy-page__block-descr">
            <strong>Eligibility.</strong> You must be at least eighteen (18)
            years of age or older to use the Services, or of the minimum legal
            age in the jurisdiction from which you access the Services.
            Additional aspects of the Services may have further age restrictions
            that will be presented to you in connection with those Services. You
            many only access the services from within the United States, with
            the exception of the states of IA, LA, MI, MO, NJ, NY, and WA, which
            prohibit Us from offering the Platform to residents of those states.
            In addition, you cannot use the Services if (1) the Office of
            Foreign Assets Control of the United States Treasury Department
            lists you as a specially designated national and/or blocked person;
            (2) the Bureau of Industry and Security of the United States
            Department of Commerce lists you on its denied persons list or lists
            of parties of concern; (3) you are located in a country that the
            U.S. government, European Union, and/or United Nations has placed
            embargo restriction on and you are not on the U.S. government,
            European Union, or United Nations list of restricted parties or
            persons (including being a Specially Designated National by the U.S.
            government); or (4) you’re on any similar list promulgated by an
            official agency or department of the United States government.
            Further, if you use the Services, you may not transfer or provide to
            us any currency, digital assets, or other items that have been
            derived from any illegal or unlawful activity. By using the
            Services, you represent and warrant that you meet all these
            requirements.
          </p>
          <p className="privacy-page__block-descr">
            For the avoidance of doubt, you shall not use or access the Website
            or Services at any time from a country that the U.S. government,
            European Union, and/or United Nations has placed embargo restriction
            on or if you are added to the U.S. government, European Union, or
            United Nations list of restricted parties or persons (including
            being a Specially Designated National by the U.S. government).
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            7. Ownership, License, and Ownership Restrictions
          </h2>
          <p className="privacy-page__block-descr">
            <strong>
              YOUR OWNERSHIP OF COLLECTIBLES WILL ONLY BE RECOGNIZED BY US IF
              YOU HAVE PURCHASED OR OTHERWISE RIGHTFULLY ACQUIRED SUCH
              COLLECTIBLES FROM A LEGITIMATE SOURCE AND NOT THROUGH ANY OF THE
              CATEGORY B PROHIBITED ACTIVITIES (AS DEFINED BELOW).
            </strong>
          </p>
          <p className="privacy-page__block-descr">
            For the purposes of this Section 7, “Own” means, with respect to a
            Collectible, a Collectible that you have purchased or otherwise
            rightfully acquired from a legitimate source (and not through any of
            the Category B Prohibited Activities (as defined below)), where
            proof of such purchase is recorded on the Polygon blockchain.
          </p>
          <p className="privacy-page__block-descr">
            <strong>Ownership of Collectible.</strong> Because each Collectible
            is an NFT on the Polygon blockchain, when you purchase a Collectible
            in accordance with these Terms (and not through any of the Category
            B Prohibited Activities), you own the underlying NFT. Ownership of
            the Collectible is mediated entirely by the Polygon blockchain.
            Except as otherwise permitted by these Terms, such as in cases where
            we determine that the Collectible has not been rightfully acquired
            from a legitimate source (including, without limitation, through any
            of the Category B Prohibited Activities), at no point will we seize,
            freeze, or otherwise modify the ownership of any Collectible.
          </p>
          <p className="privacy-page__block-descr">
            <strong>We Own the Platform.</strong> atform and the Services, and
            all intellectual property rights therein including, without
            limitation, all designs, systems, methods, information, computer
            code, software, services, “look and feel”, organization, compilation
            of the content, code, data, and all other elements of the Platform
            (collectively, the “Platform Materials”). You acknowledge that the
            Platform Materials and Third-Party Materials are protected by
            copyright, trade dress, patent, and trademark laws, international
            conventions, other relevant intellectual property and proprietary
            rights, and applicable laws. All Platform Materials and Third- Party
            Materials are the copyrighted property of us, our licensees,
            partners, or affiliates. All trademarks, service marks, and trade
            names associated with the Services or otherwise contained in the
            Platform Materials or Third-Party Materials are proprietary to us,
            our licensees, partners, or affiliates.
          </p>
          <p className="privacy-page__block-descr">
            <strong>No User License or Ownership of Platform Materials.</strong>
            No User License or Ownership of Platform Materials. Your use of the
            Services does not grant you ownership of or any other rights with
            respect to any content, code, data, or other Platform Materials or
            Third-Party Materials that you may access on or through the
            Services. We reserve all rights in and to the Platform Materials
            that are not expressly granted to you in these Terms
          </p>
          <p className="privacy-page__block-descr">
            <strong>Further User Ownership Acknowledgements.</strong> For the
            sake of clarity, you understand and agree: (a) that your purchase of
            a Collectible, whether via the Services or otherwise, does not give
            you any rights or licenses in or to the Platform Materials or any
            Third Party Materials other than those expressly contained in these
            Terms or, if applicable, the Third Party Service terms; (b) that you
            do not have the right, except as otherwise set forth in these Terms,
            to reproduce, distribute, or otherwise commercialize any elements of
            the Platform Materials without the prior written consent of our
            Chief Executive Officer, in each case, which consent may be withheld
            in our sole and absolute discretion; and (c) that you will not apply
            for, register, or otherwise use or attempt to use any of our
            trademarks or service marks, or any confusingly similar marks,
            anywhere in the world, without the prior written consent of our
            Chief Executive Officer, in each case, which consent may be withheld
            at our sole and absolute discretion.
          </p>
          <p className="privacy-page__block-descr">
            <strong>User Feedback.</strong> You may choose to submit comments,
            bug reports, ideas, or other feedback about the Services, including
            without limitation about how to improve the Services (collectively,
            “Feedback”). By submitting any Feedback, you agree that we are free
            to use such Feedback at our discretion and without any compensation
            to you, and to disclose such Feedback to third parties (whether on a
            non-confidential basis, or otherwise). You hereby grant us a
            perpetual, irrevocable, nonexclusive, worldwide license under all
            rights necessary for us to incorporate and use your Feedback for any
            purpose.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            8. Conditions of Use and Prohibited Activities
          </h2>
          <p className="privacy-page__block-descr">
            <strong>
              YOU AGREE THAT YOU ARE RESPONSIBLE FOR YOUR OWN CONDUCT WHILE
              ACCESSING OR USING THE SERVICES, AND FOR ANY CONSEQUENCES THEREOF.
              YOU AGREE TO USE THE SERVICES ONLY FOR PURPOSES THAT ARE LEGAL,
              PROPER, AND IN ACCORDANCE WITH THESE TERMS AND ANY APPLICABLE LAWS
              OR REGULATIONS..
            </strong>
          </p>
          <p className="privacy-page__block-descr">
            <strong>User Warranties.</strong> Without limiting the foregoing,
            you warrant and agree that your use of the Services will not (and
            will not allow any third party to):
          </p>
          <ul className="privacy-page__block-list">
            <li className="privacy-page__block-list-item">
              In any manner:
              <ul className="privacy-page__block-list">
                <li className="privacy-page__block-list-item">
                  Involve the sending, uploading, distributing, or disseminating
                  any unlawful, defamatory, harassing, abusive, fraudulent,
                  obscene, or otherwise objectionable content;
                </li>
                <li className="privacy-page__block-list-item">
                  Involve the distribution of any viruses, worms, defects,
                  Trojan horses, corrupted files, hoaxes, or any other items of
                  a destructive or deceptive nature;
                </li>
                <li className="privacy-page__block-list-item">
                  Involve the uploading, posting, transmitting, or otherwise
                  making available through the Services any content that
                  infringes the intellectual property rights of any party;
                </li>
                <li className="privacy-page__block-list-item">
                  Involve using the Services to violate the legal rights (such
                  as rights of privacy and publicity) of others;
                </li>
                <li className="privacy-page__block-list-item">
                  Involve engaging in, promoting, or encouraging illegal
                  activity (including, without limitation, money laundering);
                </li>
                <li className="privacy-page__block-list-item">
                  Involve interfering with other users’ enjoyment of the
                  Services;
                </li>
                <li className="privacy-page__block-list-item">
                  Involve exploiting the Services for any unauthorized purpose;
                </li>
                <li className="privacy-page__block-list-item">
                  Involve modifying, adapting, translating, or reverse
                  engineering any portion of the Services;
                </li>
                <li className="privacy-page__block-list-item">
                  Involve removing any copyright, trademark, or other
                  proprietary rights notices contained in or on the Platform or
                  any part of it;
                </li>
                <li className="privacy-page__block-list-item">
                  Involve reformatting or framing any portion of the Services;
                </li>
                <li className="privacy-page__block-list-item">
                  Involve displaying any content on the Platform, or using the
                  Services, that contains any hate-related or violent content or
                  contains any other material, products, or services that
                  violate or encourage conduct that would violate any criminal
                  laws, any other applicable laws, or any third-party rights;
                </li>
                <li className="privacy-page__block-list-item">
                  Involve using any spider, site search/retrieval application,
                  or other device to retrieve or index any portion of the
                  Platform or the content posted on the Platform, or to collect
                  information about its users for any unauthorized purpose;
                </li>
                <li className="privacy-page__block-list-item">
                  Involve accessing or using the Services for the purpose of
                  creating a product or service that is competitive with any of
                  our products or services;
                </li>
                <li className="privacy-page__block-list-item">
                  Involve abusing, harassing, or threatening another user of the
                  Services or any of our authorized representatives, customer
                  service personnel, chat board moderators, or volunteers
                  (including, without limitation, filing support tickets with
                  false information, sending excessive emails or support
                  tickets, obstructing our employees, partners, or contractors
                  from doing their jobs, refusing to follow the instructions of
                  our employees, or publicly disparaging us by implying
                  favoritism by our employees or otherwise); or
                </li>
                <li className="privacy-page__block-list-item">
                  Involve using any abusive, defamatory, ethnically or racially
                  offensive, harassing, harmful, hateful, obscene, offensive,
                  sexually explicit, threatening, or vulgar language when
                  communicating with another user of the Services or any of our
                  authorized representatives, customer service personnel, chat
                  board moderators, or volunteers (each, a “Category A
                  Prohibited Activity”); and/or
                </li>
              </ul>
            </li>
            <li className="privacy-page__block-list-item">
              In any manner:
              <ul className="privacy-page__block-list">
                <li className="privacy-page__block-list-item">
                  Involve the impersonation of another person (via the use of an
                  email address or otherwise);
                </li>
                <li className="privacy-page__block-list-item">
                  Involve using, employing, operating, or creating a computer
                  program to simulate the human behavior of a user (“Bots”);
                </li>
                <li className="privacy-page__block-list-item">
                  Involve using, employing, or operating Bots or other similar
                  forms of automation to engage in any activity or transaction
                  on the Platform;
                </li>
                <li className="privacy-page__block-list-item">
                  Involve acquiring Collectibles through inappropriate or
                  illegal means (including, among other things, using a payment
                  mechanism that you do not have the right to use, or purchasing
                  a Collectible and then attempting to charge the cost back to
                  your payment method while still maintaining ownership or
                  control of the Collectible or selling, gifting, or trading the
                  Collectible to someone else);
                </li>
                <li className="privacy-page__block-list-item">
                  Otherwise involve or result in the wrongful seizure or receipt
                  of any Collectibles or other digital assets;
                </li>
              </ul>
            </li>
          </ul>
          <p className="privacy-page__block-descr">
            <strong>Effect of Your Breaches.</strong> IF YOU ENGAGE IN ANY OF
            THE PROHIBITED ACTIVITIES, WE MAY, AT OUR SOLE AND ABSOLUTE
            DISCRETION, WITHOUT NOTICE OR LIABILITY TO YOU, AND WITHOUT LIMITING
            ANY OF OUR OTHER RIGHTS OR REMEDIES AT LAW OR IN EQUITY, (A)
            IMMEDIATELY DELETE YOUR COLLECTIBLES’ IMAGES AND DESCRIPTIONS FROM
            THE PLATFORM, OR DENY YOU ACCESS TO AND TAKE POSSESSION OF YOUR
            DIGITAL WALLET AND COLLECTIBLES, (B) DEEM ANY TRANSACTION THAT TOOK
            PLACE VIA OR AS THE RESULT OF SUCH ACTIVITIES TO BE VOID AB INITIO;
            AND/OR (C) IMMEDIATELY CONFISCATE ANY COLLECTIBLES (INCLUDING THEIR
            UNDERLYING NFTS) THAT WERE PURCHASED OR ACQUIRED AS THE RESULT OF
            SUCH ACTIVITIES.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">9. Termination</h2>
          <p className="privacy-page__block-descr">
            <strong>You Terminate.</strong> You may terminate these Terms by
            discontinuing your access to and use of the Services. If you
            terminate these Terms, you will not receive any refunds for any
            purchases that you might make on or through the Services—whether for
            Collectibles or anything else.
          </p>
          <p className="privacy-page__block-descr">
            <strong>We Terminate.</strong> You agree that we, in our sole
            discretion and for any or no reason, may terminate these Terms
            without the provision of prior notice. You agree that any suspension
            or termination of your access to the Services may be without prior
            notice, and that we will not be liable to you or to any third party
            for any such suspension or termination. If we terminate your access
            to the Services without cause we will provide the means for you to
            transfer the contents of your Wallet off of the Platform to another
            Wallet of your choosing. If we terminate your access to the Services
            for cause as outlined elsewhere in these Terms, you forfeit the
            contents of your Wallet and we will have the right to take
            possession of the contents of your Wallet without compensation to
            you.
          </p>
          <p className="privacy-page__block-descr">
            <strong>Other Remedies Available.</strong> If we terminate these
            Terms or suspend or terminate your access to or use of the Services
            due to your breach of these Terms or any suspected fraudulent,
            abusive, or illegal activity (including, without limitation, if you
            engage in any of the Prohibited Activities), then termination of
            these Terms will be in addition to any other remedies we may have at
            law or in equity.
          </p>
          <p className="privacy-page__block-descr">
            <strong>Referral to Governmental Authority.</strong> We have the
            right, without provision of prior notice, to take appropriate legal
            action, including, without limitation, referral to law enforcement
            or regulatory authority, or notifying the harmed party of any
            illegal or unauthorized use of the Services. Without limiting the
            foregoing, we have the right to fully cooperate with any law
            enforcement authorities or court order requesting or directing us to
            disclose the identity or other information of anyone using the
            Services.
          </p>
          <p className="privacy-page__block-descr">
            <strong>Effect of Termination.</strong> Upon any termination or
            expiration of these Terms, whether by you or us, you may no longer
            have access to information that you have posted on the Platform, and
            you acknowledge that we will have no obligation to maintain any such
            information in our databases or to forward any such information to
            you or to any third party. Sections 2 and 4 through 18 will survive
            the termination or expiration of these Terms for any reason.
          </p>
          <p className="privacy-page__block-descr">
            <strong>
              YOU WAIVE AND HOLD US AND OUR PARENT, SUBSIDIARIES, AFFILIATES,
              SERVICE PROVIDERS, CONTRACTORS, LICENSEES, LICENSORS, SUPPLIERS,
              AND SUCCESSORS, AND OUR AND THEIR RESPECTIVE DIRECTORS, OFFICERS,
              EMPLOYEES, AND AGENTS, HARMLESS FROM ANY AND ALL CLAIMS RESULTING
              FROM ANY ACTION TAKEN BY US AND ANY OF THE FOREGOING PARTIES
              RELATING TO ANY INVESTIGATIONS BY EITHER US OR SUCH PARTIES OR BY
              LAW ENFORCEMENT AUTHORITIES.
            </strong>
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">10. Disclaimers</h2>
          <p className="privacy-page__block-descr">
            <strong>
              YOU EXPRESSLY UNDERSTAND AND AGREE THAT YOUR ACCESS TO AND USE OF
              THE SERVICES IS AT YOUR SOLE RISK, AND THAT THE SERVICES ARE
              PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY
              KIND, WHETHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT
              PERMISSIBLE PURSUANT TO APPLICABLE LAW, WE, OUR SUBSIDIARIES, AND
              AFFILIATES MAKE NO EXPRESS WARRANTIES AND HEREBY DISCLAIM ALL
              IMPLIED WARRANTIES REGARDING THE SERVICES AND ANY PART OF THEM,
              INCLUDING THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE, NON-INFRINGEMENT, CORRECTNESS, ACCURACY, OR
              RELIABILITY. WITHOUT LIMITING THE GENERALITY OF THE FOREGOING, WE,
              OUR PARENT, SUBSIDIARIES, AND AFFILIATES DO NOT REPRESENT OR
              WARRANT TO YOU THAT: (I) YOUR ACCESS TO OR USE OF THE SERVICES
              WILL MEET YOUR REQUIREMENTS; (II) YOUR ACCESS TO OR USE OF THE
              SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE OR FREE FROM ERROR;
              (III) USAGE DATA PROVIDED THROUGH THE SERVICES WILL BE ACCURATE;
              (IV) THE SERVICES OR ANY CONTENT, SERVICES, OR FEATURES MADE
              AVAILABLE ON OR THROUGH THE SERVICES ARE FREE OF VIRUSES OR OTHER
              HARMFUL COMPONENTS; OR (V) THAT ANY DATA THAT YOU DISCLOSE WHEN
              YOU USE THE SERVICES WILL BE SECURE. SOME JURISDICTIONS DO NOT
              ALLOW THE EXCLUSION OF IMPLIED WARRANTIES IN CONTRACTS WITH
              CONSUMERS, SO SOME OR ALL OF THE ABOVE EXCLUSIONS MAY NOT APPLY TO
              YOU.
            </strong>
          </p>
          <p className="privacy-page__block-descr">
            YOU ACCEPT THE INHERENT SECURITY RISKS OF PROVIDING INFORMATION AND
            DEALING ONLINE OVER THE INTERNET AND AGREE THAT WE HAVE NO LIABILITY
            OR RESPONSIBILITY FOR ANY BREACH OF SECURITY UNLESS IT IS DUE TO OUR
            GROSS NEGLIGENCE.
          </p>
          <p className="privacy-page__block-descr">
            WE WILL NOT BE RESPONSIBLE OR LIABLE TO YOU FOR ANY LOSSES YOU INCUR
            AS THE RESULT OF YOUR USE OF THE POLYGON BLOCKCHAIN, OR YOUR DIGITAL
            WALLET, INCLUDING BUT NOT LIMITED TO ANY LOSSES, DAMAGES OR CLAIMS
            ARISING FROM: (I) USER ERROR, SUCH AS FORGOTTEN PASSWORDS OR
            INCORRECTLY CONSTRUED SMART CONTRACTS OR OTHER TRANSACTIONS; (II)
            SERVER FAILURE OR DATA LOSS; (III) CORRUPTED WALLET FILES; OR (IV)
            UNAUTHORIZED ACCESS OR ACTIVITIES BY THIRD PARTIES, INCLUDING, BUT
            NOT LIMITED TO, THE USE OF VIRUSES, PHISHING, BRUTE-FORCING OR OTHER
            MEANS OF ATTACK AGAINST THE PLATFORM, THE POLYGON BLOCKCHAIN, OR ANY
            DIGITAL WALLET.
          </p>
          <p className="privacy-page__block-descr">
            COLLECTIBLES ARE INTANGIBLE DIGITAL ASSETS THAT EXIST ONLY BY VIRTUE
            OF THE OWNERSHIP RECORD MAINTAINED IN THE POLYGON BLOCKCHAIN. ALL
            SMART CONTRACTS ARE CONDUCTED AND OCCUR ON THE DECENTRALIZED LEDGER
            WITHIN THE POLYGON BLOCKCHAIN. WE HAVE NO CONTROL OVER AND MAKE NO
            GUARANTEES OR PROMISES WITH RESPECT TO SMART CONTRACTS.
          </p>
          <p className="privacy-page__block-descr">
            COLLECTIBLES ARE INTENDED FOR CONSUMER ENJOYMENT, USE AND
            CONSUMPTION ONLY. A COLLECTIBLE IS NOT A “SECURITY,” AS DEFINED
            UNDER THE SECURITIES ACT OF 1933, AS AMENDED, THE SECURITIES
            EXCHANGE ACT OF 1934, AS AMENDED, THE INVESTMENT COMPANY ACT OF
            1940, AS AMENDED, OR UNDER THE SECURITIES LAWS OF ANY U.S. STATE.
          </p>
          <p className="privacy-page__block-descr">
            WE ARE NOT RESPONSIBLE FOR LOSSES DUE TO BLOCKCHAINS OR ANY OTHER
            FEATURES OF THE POLYGON BLOCKCHAIN, OR ANY DIGITAL WALLET,
            INCLUDING, FORKS, TECHNICAL NODE ISSUES, OR ANY OTHER ISSUES HAVING
            FUND LOSSES AS A RESULT.
          </p>
          <p className="privacy-page__block-descr">
            THE PLATFORM OFFERS THE SERVICES AS SWEEPSTAKES/PROMOTIONAL GAMES.
            THE GAMES CAN BE PLAYED FOR FREE AND REQUIRE NO PURCHASES TO PLAY.
            THE PLATFORM OFFERS VERSIONS OF THE GAMES ON THE PLATFORM WHICH
            REQUIRE THE PURCHASE OF CARD PACKS TO PLAY THE GAMES. THESE GAMES
            MAY OFFER CASH PRIZES FOR PLAYERS WHO USE THEIR SKILL AND KNOWLEDGE
            TO WIN THE GAMES. IT IS POSSIBLE THAT CERTAIN JURISDICTIONS MAY
            CONSIDER THAT CERTAIN OF THE SERVICES CONSTITUTE FANTASY SPORTS OR
            GAMBLING AND ARE GOVERNED BY APPLICABLE STATE STATUTES AND
            REGULATIONS. WHEN PARTICIPANTS CREATE, TRADE, BUY OR SELL THEIR
            COLLECTIBLES, THEY ARE UTILIZING SKILL TO CHOOSE THE COLLECTIBLE TO
            CREATE, BUY, TRADE OR SELL AND VARIOUS OTHER FACTORS. SWEEPSTAKE AND
            PROMOTIONAL GAMES OF SKILL ARE PERMITTED IN MOST JURISDICTIONS AND
            DO NOT CONSTITUTE GAMBLING (WHICH GENERALLY REQUIRES A GAME OF
            CHANCE, PRIZE, AND CONSIDERATION TO PLAY). SOME JURISDICTIONS LIMIT
            GAMES OF SKILL WHERE PAYMENTS ARE INVOLVED. IT IS YOUR
            RESPONSIBILITY TO DETERMINE WHETHER THE JURISDICTION IN WHICH YOU
            ARE LOCATED PERMITS SKILL-BASED COMPETITIONS. WHEN YOU PARTICIPATE
            IN COMPETITIONS, THESE TERMS SHALL APPLY.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            11. Limitation of Liability
          </h2>
          <p className="privacy-page__block-descr">
            YOU UNDERSTAND AND AGREE THAT WE AND OUR PARENT, SUBSIDIARIES,
            AFFILIATES, SERVICE PROVIDERS, CONTRACTORS, LICENSEES, LICENSORS,
            SUPPLIERS, AND SUCCESSORS, AND OUR AND THEIR RESPECTIVE DIRECTORS,
            OFFICERS, EMPLOYEES, AND AGENTS, WILL NOT BE LIABLE TO YOU OR TO ANY
            THIRD PARTY FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
            EXEMPLARY DAMAGES WHICH YOU MAY INCUR, HOWSOEVER CAUSED AND UNDER
            ANY THEORY OF LIABILITY, INCLUDING, WITHOUT LIMITATION, ANY
            ECONOMINIC LOSS (WHETHER INCURRED DIRECTLY OR INDIRECTLY), LOSS OF
            GOODWILL OR BUSINESS REPUTATION, LOSS OF DATA, COST OF PROCUREMENT
            OF SUBSTITUTE GOODS OR SERVICES, DIMINUTION OF VALUE OR ANY OTHER
            INTANGIBLE LOSS, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF
            SUCH DAMAGES.
          </p>
          <p className="privacy-page__block-descr">
            YOU AGREE THAT OUR AND OUR PARENTS’, SUBSIDIARIES’, AND AFFILIATES’
            TOTAL, AGGREGATE LIABILITY TO YOU FOR ANY AND ALL CLAIMS ARISING OUT
            OF OR RELATING TO THESE TERMS OR YOUR ACCESS TO OR USE OF (OR YOUR
            INABILITY TO ACCESS OR USE) ANY PORTION OF THE SERVICES, WHETHER IN
            CONTRACT, TORT, STRICT LIABILITY, OR ANY OTHER LEGAL THEORY, IS
            LIMITED TO THE GREATER OF (I) THE AMOUNTS YOU HAVE ACTUALLY AND
            LAWFULLY PAID US UNDER THESE TERMS IN THE TWO (2) MONTH PERIOD
            PRECEDING THE DATE THE CLAIM AROSE, OR (II) $250 U.S. DOLLARS.
          </p>
          <p className="privacy-page__block-descr">
            YOU ACKNOWLEDGE AND AGREE THAT WE HAVE MADE THE SERVICES AVAILABLE
            TO YOU AND ENTERED INTO THESE TERMS IN RELIANCE UPON THE
            REPRESENTATIONS AND WARRANTIES, DISCLAIMERS AND LIMITATIONS OF
            LIABILITY SET FORTH HEREIN, WHICH REFLECT A REASONABLE AND FAIR
            ALLOCATION OF RISK BETWEEN US AND YOU AND FORM AN ESSENTIAL BASIS OF
            THE BARGAIN BETWEEN US AND YOU. WE WOULD NOT BE ABLE TO PROVIDE THE
            SERVICES TO YOU WITHOUT THESE LIMITATIONS.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">12. Assumption of Risk</h2>
          <p className="privacy-page__block-descr">
            (i) <strong>Value and Volatility.</strong> The prices of both
            collectible and non-collectible blockchain assets are extremely
            volatile and subjective and collectible blockchain assets have no
            inherent or intrinsic value. Fluctuations in the price of other
            digital assets could materially and adversely affect the value of
            your Collectibles, which may also be subject to significant price
            volatility. We cannot guarantee that any Collectibles purchased,
            will retain their original value, as the value of collectibles is
            inherently subjective and factors occurring outside of the Platform
            may materially impact the value and desirability of any particular
            Collectible.
          </p>
          <p className="privacy-page__block-descr">
            (ii) <strong>Tax Calculations.</strong> You are solely responsible
            for determining what, if any, taxes apply to your
            Collectible-related transactions. We are not responsible for
            determining the taxes that apply to your transactions, both on and
            off the Platform
          </p>
          <p className="privacy-page__block-descr">
            (iii) <strong>Inherent Risks with Internet Currency.</strong> There
            are risks associated with using an Internet-based currency,
            including, but not limited to, the risk of hardware, software and
            Internet connections, the risk of malicious software introduction,
            and the risk that third parties may obtain unauthorized access to
            information stored within your digital wallet. You accept and
            acknowledge that we will not be responsible for any communication
            failures, disruptions, errors, distortions or delays you may
            experience when using the Polygon blockchain, however caused.
          </p>
          <p className="privacy-page__block-descr">
            (iv) <strong>Regulatory Uncertainty.</strong> The regulatory regime
            governing blockchain technologies and digital assets, is uncertain,
            and new regulations or policies may materially adversely affect the
            development of the Platform, your use and access of the Platform,
            and therefore the potential utility or value of your Collectibles.
          </p>
          <p className="privacy-page__block-descr">
            (v) <strong>Software Risks.</strong> Upgrades to the Polygon
            blockchain, a hard fork in the Polygon blockchain, or a change in
            how transactions are confirmed on the Polygon blockchain may have
            unintended, adverse effects any network on the Polygon blockchain,
            including the Platform.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">13. Indemnification</h2>
          <p className="privacy-page__block-descr">
            You agree to hold harmless and indemnify us and our parent,
            subsidiaries, affiliates, partners, suppliers, service providers,
            and each of their respective, officers, directors, employees and
            agents, from and against any claim, liability, loss, damage (actual
            and consequential) of any kind or nature, suit, judgment, litigation
            cost and attorneys' fees arising out of or in any way related to:
            (i) your breach of these Terms; (ii) your misuse of the Services; or
            (iii) your violation of applicable laws, rules or regulations in
            connection with your access to or use of the Services. You agree
            that we will have control of the defense or settlement of any such
            claims.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            14. Third-Party Services, Materials and External Sites
          </h2>
          <p className="privacy-page__block-descr">
            Third Party Services are provided solely as a convenience to our
            users. Any content, applications, advertising, data, and statistics
            provided through Third Party Services (“Third-Party Materials”), or
            made available through links to certain third-party websites
            (collectively, the “External Sites”), are provided solely as a
            convenience to our users. By using the Services, you acknowledge and
            agree that we are not responsible for examining or evaluating the
            content, accuracy, completeness, availability, timeliness, validity,
            copyright compliance, legality, decency, quality, or any other
            aspect of such Third-Party Materials or External Sites, and have no
            control over the same. You acknowledge and agree that we are not
            responsible for the availability of any External Sites, and that we
            do not endorse any advertising, products or other Third-Party
            Materials on or made available from or through any External Sites.
            Furthermore, you acknowledge and agree that we are not liable for
            any loss or damage which may be incurred as a result of the
            availability or unavailability of the External Sites, or as a result
            of any reliance placed by you upon the completeness, accuracy or
            existence of any advertising, products or other Third Party
            Materials on the Platform, or on or made available from any External
            Sites. Third-Party Materials and links to External Sites are
            provided solely as a convenience to you.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">15. Force Majeure</h2>
          <p className="privacy-page__block-descr">
            (i) <strong>Force Majeure Events.</strong> We will not be liable or
            responsible to the you, nor be deemed to have defaulted under or
            breached these Terms, for any failure or delay in fulfilling or
            performing any of these Terms, when and to the extent such failure
            or delay is caused by or results from the following force majeure
            events (“Force Majeure Event(s)”): (a) acts of God; (b) flood, fire,
            earthquake, epidemics, pandemics, including the 2019 novel
            coronavirus pandemic (COVID-19), tsunami, explosion; (c) war,
            invasion, hostilities (whether war is declared or not), terrorist
            threats or acts, riot or other civil unrest; (d) government order,
            law, or action; (e) embargoes or blockades in effect on or after the
            date of this agreement; (f) strikes, labor stoppages or slowdowns or
            other industrial disturbances; (g) shortage of adequate or suitable
            Internet connectivity, telecommunication breakdown or shortage of
            adequate power or electricity; and (h) other similar events beyond
            our control.
          </p>
          <p className="privacy-page__block-descr">
            (ii) <strong>Performance During Force Majeure Events.</strong> If we
            suffer a Force Majeure Event, we will use reasonable efforts to
            promptly notify you of the Force Majeure Event, stating the period
            of time the occurrence is expected to continue. We will use diligent
            efforts to end the failure or delay and ensure the effects of such
            Force Majeure Event are minimized. We will resume the performance of
            our obligations as soon as reasonably practicable after the removal
            of the cause. In the event that our failure or delay remains uncured
            for a period of forty-five (45) consecutive days following written
            notice given by us under this Section, we may thereafter terminate
            these Terms upon fifteen (15) days' written notice.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">
            17. Dispute Resolution; Binding Arbitration; Class Action Waiver
          </h2>
          <p className="privacy-page__block-descr">
            <strong>
              IN THIS SECTION 17, YOU ARE AGREEING TO GIVE UP RIGHTS TO LITIGATE
              CLAIMS IN A COURT. OTHER RIGHTS THAT YOU WOULD HAVE IF YOU WENT TO
              COURT MAY ALSO BE UNAVAILABLE OR MAY BE LIMITED IN ARBITRATION.
            </strong>
          </p>
          <p className="privacy-page__block-descr">
            <strong>
              YOU HEREBY EXPRESSLY GIVE UP YOUR RIGHT TO HAVE A TRIAL BY JURY.
            </strong>
          </p>
          <p className="privacy-page__block-descr">
            <strong>
              YOU HEREBY EXPRESSLY GIVE UP YOUR RIGHT TO PARTICIPATE AS A MEMBER
              OF A CLASS OF CLAIMANTS IN ANY LAWSUIT, INCLUDING, BUT NOT LIMITED
              TO, CLASS ACTION LAWSUITS INVOLVING ANY SUCH DISPUTE.
            </strong>
          </p>
          <p className="privacy-page__block-descr">
            (i) <strong>Binding Arbitration.</strong> All disputes arising out
            of or in connection with this contract, or in respect of any defined
            legal relationship associated therewith or derived therefrom, shall
            be referred to and finally resolved by arbitration administered by
            JAMS under its Streamlined Arbitration Rules and Procedures and the
            JAMS Consumer Arbitration Minimum Standards (together, the “Rules”).
            The most recent version of the Rules is available at
            <a
              href="https://www.jamsadr.com/rules-download"
              target="_blank"
              rel="noreferrer"
            >
              www.jamsadr.com/rules-download
            </a>{" "}
            and are hereby incorporated by reference. The place of arbitration
            shall be Boca Raton, Florida, unless the arbitrator determines that
            the dispute can be resolved on the submission of written papers or
            you exercise your right to an in-person hearing in your hometown
            area.
          </p>
          <p className="privacy-page__block-descr">
            (ii) <strong>Arbitration Fees.</strong> You and we agree that for
            any arbitration you initiate, you will pay the filing fee (up to a
            maximum of $250 if you are a consumer), and we will pay the
            remaining JAMS fees and costs. For any arbitration initiated by us,
            we will pay all JAMS fees and costs.
          </p>
          <p className="privacy-page__block-descr">
            (iii) <strong>Award Enforcement.</strong> The award of the
            arbitrator will be final and binding, and any judgment on the award
            rendered by the arbitrator may be entered in any court of competent
            jurisdiction. The parties agree that they will not appeal any
            arbitration decision to any court.
          </p>
          <p className="privacy-page__block-descr">
            (iv) <strong>Additional Remedies.</strong> Notwithstanding the
            foregoing, we may seek and obtain injunctive relief in any
            jurisdiction in any court of competent jurisdiction, and you agree
            that these Terms are specifically enforceable by us through
            injunctive relief and other equitable remedies without proof of
            monetary damages.
          </p>
          <p className="privacy-page__block-descr">
            (v) <strong>Opt Out.</strong> You have the right to opt out of
            binding arbitration within 30 days of the date you first accepted
            the terms of this Section 17 by submitting a request at{" "}
            <a
              href="mailto:support@twotwentyk.io"
              className="privacy-page__block-link"
            >
              support@twotwentyk.io
            </a>
            . In order to be effective, the opt-out notice must include your
            full name and address and clearly indicate your intent to opt out of
            binding arbitration.
          </p>
          <p className="privacy-page__block-descr">
            (vi) <strong>Confidentiality.</strong> You and us agree that any
            claim that has been submitted to arbitration, and all related
            proceedings, including any settlement agreement, shall be kept
            confidential. In the event you or we file the arbitration award with
            any court of competent jurisdiction in order to have that award
            confirmed in a court order or judgment, or any other proceeding
            under the Federal Arbitration Act, then each party shall take
            measures to the extent permitted by applicable law to redact or file
            under seal any highly sensitive business or personal information
            that appear in the award and any other supporting documents filed in
            such a proceeding.
          </p>
          <p className="privacy-page__block-descr">
            (vii) <strong>Severability.</strong> If any portion of this Section
            is found to be unenforceable or unlawful for any reason, (a) the
            unenforceable or unlawful provision shall be severed from these
            Terms; (b) severance of the unenforceable or unlawful provision
            shall have no impact whatsoever on the remainder of this Section or
            the parties' ability to compel arbitration of any remaining claims
            on an individual basis pursuant to this Section ; and (c) to the
            extent that any claims must therefore proceed on a class,
            collective, consolidated, or representative basis, such claims must
            be litigated in a civil court of competent jurisdiction and not in
            arbitration, and the parties agree that litigation of those claims
            shall be stayed pending the outcome of any individual claims in
            arbitration. Further, if any part of this Section is found to
            prohibit an individual claim seeking public injunctive relief, that
            provision will have no effect to the extent such relief is allowed
            to be sought out of arbitration, and the remainder of this section
            will be enforceable.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">18. General</h2>
          <p className="privacy-page__block-descr">
            (i) <strong>Entire Agreement.</strong> These Terms and our Privacy
            Policy constitute the entire legal agreement between you and us and
            will be deemed to be the final and integrated agreement between you
            and us, and govern your access to and use of the Services, and
            completely replace any prior or contemporaneous agreements between
            you and us related to your access to or use of the Services, whether
            oral or written.
          </p>
          <p className="privacy-page__block-descr">
            (ii) <strong>Third-Party Beneficiaries.</strong> Other than as
            otherwise expressly set forth herein, these Terms do not and are not
            intended to confer any rights or remedies upon any person or entity
            other than you.
          </p>
          <p className="privacy-page__block-descr">
            (iii) <strong>Interpretation.</strong> The language in these Terms
            will be interpreted as to its fair meaning, and not strictly for or
            against any party.
          </p>
          <p className="privacy-page__block-descr">
            (iv) <strong>Severability.</strong> Should any part of these Terms
            be held invalid, illegal, void or unenforceable, that portion will
            be deemed severed from these Terms and will not affect the validity
            or enforceability of the remaining provisions of these Terms.
          </p>
          <p className="privacy-page__block-descr">
            (v) <strong>No Waivers.</strong> Our failure or delay to exercise or
            enforce any right or provision of these Terms will not constitute or
            be deemed a waiver of future exercise or enforcement of such right
            or provision. The waiver of any right or provision of these Terms
            will be effective only if in writing and signed for and on behalf of
            us by a duly authorized representative.
          </p>
          <p className="privacy-page__block-descr">
            (vi) <strong>Governing Law.</strong> All matters arising out of or
            relating to these Terms will be governed by and construed in
            accordance with the laws of the state of Delaware and the federal
            laws of the United States applicable therein without giving effect
            to any choice or conflict of law provision or rule (whether of the
            state of Florida or any other jurisdiction).
          </p>
          <p className="privacy-page__block-descr">
            (vii) <strong>Venue.</strong> Subject to Section 17 of these Terms,
            any legal action or proceeding arising under these Terms will be
            brought exclusively in state or federal courts located in Boca
            Raton, Florida, and we and you irrevocably consent and attorn to the
            personal jurisdiction and venue there.
          </p>
          <p className="privacy-page__block-descr">
            (viii) <strong>Notices.</strong> We may provide you with any notices
            (including, without limitation those regarding changes to these
            Terms) by email or postings on the Platform. By providing us with
            your email address, you consent to our using the email address to
            send you any notices. Notices sent by email will be effective when
            we send the email, and notices we provide by posting will be
            effective upon posting. It is your responsibility to keep your email
            address current.
          </p>
          <p className="privacy-page__block-descr">
            (ix) <strong>Assignment.</strong> You may not assign any of your
            rights or obligations under these Terms, whether by operation of law
            or otherwise, without our prior written consent. We may assign our
            rights and obligations under these Terms in our sole discretion to
            an affiliate, or in connection with an acquisition, sale or merger.
            These Terms shall be binding upon and inure to the benefit of the
            applicable parties and their successors and permitted assigns.
          </p>
          <p className="privacy-page__block-descr">
            (x) <strong>California Residents.</strong> If you are a California
            resident, in accordance with Cal. Civ. Code § 1789.3, you may report
            complaints to the Complaint Assistance Unit of the Division of
            Consumer Services of the California Department of Consumer Affairs
            by contacting them in writing at 1625 North Market Blvd., Suite N
            112 Sacramento, CA 95834, or by telephone at (800) 952-5210.
          </p>
          <p className="privacy-page__block-descr">
            (xi) <strong>Export Laws.</strong> You agree that you will not
            export or re-export, directly or indirectly, the Services and/or
            other information or materials provided by us hereunder, to any
            country for which the United States or any other relevant
            jurisdiction requires any export license or other governmental
            approval at the time of export without first obtaining such license
            or approval. In particular, but without limitation, the Services may
            not be exported or re- exported (a) into any U.S. embargoed
            countries or any country that has been designated by the U.S.
            Government as a “terrorist supporting” country, or (b) to anyone
            listed on any U.S. Government list of prohibited or restricted
            parties, including the U.S. Treasury Department’s list of Specially
            Designated Nationals or the U.S. Department of Commerce Denied
            Person’s List or Entity List. You warrant and represent that you’re
            not located in, under the control of, or a national or resident of
            any embargoed country. By using the Services, you represent and
            warrant that you are not located in any such country or on any such
            list. You are responsible for and hereby agree to comply at your
            sole expense with all applicable United States export laws and
            regulations.
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">How to Contact Us.</h2>
          <p className="privacy-page__block-descr">
            <strong>How to Contact Us.</strong> How to Contact Us. You may
            contact us regarding the Services or these Terms by phone at
            585-500-1792 or by e-mail at
            <a
              href="mailto:support@twotwentyk.io"
              className="privacy-page__block-link"
            >
              support@twotwentyk.io
            </a>
          </p>
        </section>

        <section className="privacy-page__block">
          <h2 className="privacy-page__block-title">Changes to These Terms</h2>
          <p className="privacy-page__block-descr">
            We may change these Terms to reflect updates to our business
            processes, acceptable practices, or legislative changes. Updated
            Terms will apply only to information collected after the change
            date.
          </p>
        </section>
      </main>
    </article>
  );
};

export default Terms;
